<script lang="ts">
import { defineComponent } from 'vue'
import Container from 'typedi'
import { debounce } from 'lodash'
import Notification from '@/modules/common/services/notification.service'
import { PrintQueueItem } from '@/modules/level-two/models'
import BadgePrintProviderMixin from '@/modules/common/components/ui-core/gtr-badge-printer/badge-print-provider.mixin'
import BadgePrintQueueMixin from '@/modules/common/components/ui-core/gtr-badge-printer/badge-print-queue.mixin'
import GtrBadgePreview from '@/modules/common/components/ui-core/gtr-badge-preview/gtr-badge-preview.vue'
import PrinterSelect from '@/modules/common/components/ui-core/gtr-badge-printer/printer-select.vue'
import PrintOrderControl from '@/modules/common/components/ui-core/gtr-badge-printer/print-order-control.vue'
import NotesWarningModal from '@/modules/common/components/ui-core/gtr-badge-printer/notes-warning-modal.vue'

const SEARCH_RESULT_CAP = 25
const SEARCH_DEBOUNCE_MS = 300
const SEARCH_MIN_CHARS = 3

const OPERATOR_LABELS: Record<string, string> = {
  '=': '=',
  '!=': '!=',
  '>': '>',
  '<': '<',
  '>=': '>=',
  '<=': '<=',
  '^=': 'contains',
  '!^=': 'does not contain',
  '@=': 'contains item',
  '=null': 'is empty',
  '!=null': 'is not empty',
  attended: 'attended',
  not_attended: 'did not attend',
  sent: 'was sent',
  not_sent: 'was not sent'
}

const NO_VALUE_OPERATORS = ['=null', '!=null', 'attended', 'not_attended', 'sent', 'not_sent']

function defaultCriteria (): any {
  return {
    visible: true,
    global_type: '',
    type: '',
    field: '',
    operator: '',
    value: '',
    group_operator: '',
    group_items: []
  }
}

export default defineComponent({
  name: 'GtrBadgeSearchAndPrint',

  mixins: [BadgePrintProviderMixin, BadgePrintQueueMixin],

  components: {
    GtrBadgePreview,
    PrinterSelect,
    PrintOrderControl,
    NotesWarningModal
  },

  // #region Props

  props: {
    disabled: { required: false, type: Boolean, default: false }
  },

  // #region Data

  data () {
    return {
      showModal: false,
      initializing: false,
      activeTab: 0,

      // Single Attendee tab
      searchTerm: '',
      searchResults: [] as any[],
      searching: false,
      hasSearched: false,
      tooManyResults: false,
      searchRequestId: 0,
      selectedParticipant: null as any | null,
      badgePreviewSvg: null as string | null,
      loadingPreview: false,
      printResult: null as { success: boolean, message?: string } | null,
      debouncedSearch: null as (() => void) | null,

      // Print Multiple tab
      criteria: defaultCriteria(),
      hasPreviewed: false,
      previewLoading: false,
      previewError: '',
      previewTotal: 0,
      previewStale: false,
      printQueue: [] as PrintQueueItem[]
    }
  },

  // #region Watchers

  watch: {
    criteria: {
      deep: true,
      handler () {
        if (this.hasPreviewed) {
          this.previewStale = true
        }
      }
    },

    // Dismiss on full success only; any error leaves the modal open so the failure is not missed
    jobDone (done: boolean) {
      if (done && this.errors === 0) {
        this.scheduleAutoClose(() => { this.showModal = false })
      }
    },

    printResult (result: { success: boolean } | null) {
      if (result?.success) {
        this.scheduleAutoClose(() => {
          if (!this.printing) this.showModal = false
        })
      }
    },

    // The admin has moved on to another attendee or tab, so the modal must stay open under them
    searchTerm () { this.cancelAutoClose() },
    selectedParticipant () { this.cancelAutoClose() },
    activeTab () { this.cancelAutoClose() }
  },

  // #region Lifecycle Hooks

  created () {
    this.debouncedSearch = debounce(this.performSearch, SEARCH_DEBOUNCE_MS)
  },

  // #region Computed

  computed: {
    queuedToPrintCount (): number {
      return this.jobStarted ? this.remainingToPrint.length : this.itemsToPrint.length
    },

    queuedDrillItems (): PrintQueueItem[] {
      return this.jobStarted ? this.remainingToPrint : this.itemsToPrint
    },

    activeMultiDrillItems (): PrintQueueItem[] | null {
      if (this.expandedDrill === 'primary') return this.queuedDrillItems
      if (this.expandedDrill === 'secondary') return this.printedItems
      return null
    },

    canStartBulkPrint (): boolean {
      return this.hasPreviewed && this.previewTotal > 0 && !this.previewStale
    },

    criteriaSummary (): string {
      const description = this.describeCriteria(this.criteria)
      return description ? `Matching ${description}` : 'No criteria selected'
    }
  },

  // #region Methods

  methods: {
    async openDialog () {
      if (this.initializing || this.printLoopActive || this.printing) return

      this.resetState()
      this.showModal = true // acknowledge the click right away; the print service check below can take a while
      if (this.badgesEnabledAndAllowed) {
        this.initializing = true
        try {
          await this.$nextTick() // let the provider component switch (script-x/momentive-print) settle first
          await this.initializePrintService()
        } finally {
          this.initializing = false
        }
      }
    },

    closeModal () {
      // Escape bypasses the hidden close button, so guard here as well as with :closable.
      // A paused job may be closed even while its last item settles, so a hung print service cannot trap the user.
      if (this.printing) return
      this.showModal = false
    },

    /**
     * Reset both tabs' search/selection/print state and the active tab so reopening the
     * modal never shows stale results or a stale in-progress criteria selection.
     */
    resetState () {
      this.activeTab = 0
      this.searchTerm = ''
      this.searchResults = []
      this.searching = false
      this.hasSearched = false
      this.tooManyResults = false
      this.selectedParticipant = null
      this.badgePreviewSvg = null
      this.loadingPreview = false
      this.printing = false
      this.printResult = null

      this.criteria = defaultCriteria()
      this.hasPreviewed = false
      this.previewLoading = false
      this.previewError = ''
      this.previewTotal = 0
      this.previewStale = false
      this.printQueue = []
      this.resetPrintProgress()
    },

    cancel () {
      this.showModal = false
    },

    /**
     * Fires on every keystroke: clears results below the minimum length, otherwise debounces the actual search
     */
    onSearchInput () {
      if (this.searchTerm.trim().length < SEARCH_MIN_CHARS) {
        this.searchResults = []
        this.hasSearched = false
        this.tooManyResults = false
        this.selectedParticipant = null
        this.badgePreviewSvg = null
        this.loadingPreview = false
        return
      }
      this.debouncedSearch?.()
    },

    /**
     * Search participants by keyword, guarding against out-of-order responses from fast typing
     */
    async performSearch () {
      const keyword = this.searchTerm.trim()
      if (keyword.length < SEARCH_MIN_CHARS) return

      const requestId = ++this.searchRequestId
      this.searching = true

      try {
        const results = await this.$store.dispatch('attendee/searchParticipantsSimple', {
          event_uuid: this.eventUuid,
          keyword,
          limit: SEARCH_RESULT_CAP + 1
        })
        if (requestId !== this.searchRequestId) return // a newer search superseded this one

        const list = Array.isArray(results) ? results : []
        this.hasSearched = true
        this.tooManyResults = list.length > SEARCH_RESULT_CAP
        this.searchResults = this.tooManyResults ? [] : list
      } catch (error) {
        if (requestId !== this.searchRequestId) return
        Container.get(Notification).error('Error searching participants.')
        this.hasSearched = true
        this.searchResults = []
      } finally {
        if (requestId === this.searchRequestId) {
          this.searching = false
        }
      }
    },

    selectParticipant (item: any) {
      this.selectedParticipant = item
      this.fetchBadgePreview()
    },

    async fetchBadgePreview () {
      if (!this.selectedParticipant) return
      this.badgePreviewSvg = null
      this.loadingPreview = true
      try {
        const response = await this.fetchAttendeeBadgeSvg(this.selectedParticipant.id)
        this.badgePreviewSvg = response?.svg ?? null
      } catch (error) {
        this.badgePreviewSvg = null
      } finally {
        this.loadingPreview = false
      }
    },

    isSelected (item: any): boolean {
      return this.selectedParticipant?.id === item.id
    },

    /**
     * A participant is ineligible if the event requires Complete status and/or Paid payment status
     * and this participant doesn't meet the criteria that's turned on. Informational only - does not
     * block selection or printing.
     */
    isEligible (item: any): boolean {
      if ((this as any).settingBool('sync_status_complete') && item.status !== 'Complete') return false
      if ((this as any).settingBool('sync_payment_status_paid') && item.payment_status !== 'Paid') return false
      return true
    },

    async printBadge () {
      // notesCheckBusy covers a second click while the notes fetch is in flight or the notes prompt is open
      if (!this.selectedParticipant || this.printing || (this as any).notesCheckBusy) return

      this.cancelAutoClose()

      const sp = this.selectedParticipant
      const notesItem: { participant_uuid: string, notes?: string } = { participant_uuid: sp.id }
      if (sp.notes != null) notesItem.notes = String(sp.notes)
      // notesConfirming is the same-tick part of notesCheckBusy: raised before the first await, cleared once the decision is known
      ;(this as any).notesConfirming = true
      let decision: 'proceed' | 'cancel'
      try {
        decision = await (this as any).confirmSingleBadgePrint(notesItem, true)
      } finally {
        (this as any).notesConfirming = false
      }
      if (decision === 'cancel') return

      this.printing = true

      const item: PrintQueueItem = {
        participant_uuid: sp.id,
        full_name: sp.full_name,
        email: sp.email,
        company: sp.company,
        registration_type: sp.registration_type
      }
      // Reuse the SVG already fetched for the preview so printItem skips a second fetch
      if (this.badgePreviewSvg && this.selectedParticipant === sp) {
        item.content = this.badgePreviewSvg
      }

      let result: { success: boolean, stage?: string, message?: string }
      try {
        result = await (this as any).printItem(item)
      } catch (error) {
        result = { success: false, message: (error as Error)?.message }
      } finally {
        this.printing = false
      }

      if (result.success) {
        this.printResult = { success: true }
      } else {
        const message = result.stage === 'none' ? 'nothing to print' : (result.message || 'an unknown error occurred')
        this.printResult = { success: false, message }
        Container.get(Notification).error(`Print error for ${this.selectedParticipant.full_name}: ${message}`)
      }
    },

    // #region Print Multiple

    /**
     * gtr-show-if emits Date objects for datetime fields (both the single field and every
     * nested group item) rather than strings - normalize recursively before POSTing.
     */
    normalizeCriteria (node: any): any {
      if (!node || typeof node !== 'object') return node
      const normalized: any = { ...node }
      if (normalized.value instanceof Date) {
        normalized.value = normalized.value.toISOString()
      }
      if (Array.isArray(normalized.group_items)) {
        normalized.group_items = normalized.group_items.map((item: any) => this.normalizeCriteria(item))
      }
      return normalized
    },

    humanizeField (field: string): string {
      if (!field) return ''
      return field.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
    },

    describeCriteria (node: any): string {
      if (!node || !node.type) return ''
      if (node.type === '*') return 'Everyone'
      if (node.type === 'grouping') {
        const joiner = node.group_operator === 'OR' ? ' or ' : ' and '
        const parts = (node.group_items || []).map((item: any) => this.describeCriteria(item)).filter(Boolean)
        if (!parts.length) return ''
        return parts.length > 1 ? `(${parts.join(joiner)})` : parts[0]
      }
      const fieldLabel = this.humanizeField(node.field)
      if (!fieldLabel) return ''
      const opLabel = OPERATOR_LABELS[node.operator] || node.operator
      if (NO_VALUE_OPERATORS.includes(node.operator)) {
        return `${fieldLabel} ${opLabel}`
      }
      const value = node.value instanceof Date ? node.value.toLocaleDateString() : node.value
      return `${fieldLabel} ${opLabel} ${value}`
    },

    /**
     * Load the Queued to Print count (and the records behind it) for the current criteria.
     * Explicit button rather than auto-refresh: gtr-show-if's @Watch('model') is not deep, so
     * show-if-change fires essentially once on mount - an auto-refresh would need our own deep
     * watcher and would fire on every intermediate, invalid tree.
     */
    async previewCount () {
      this.previewLoading = true
      this.previewError = ''
      try {
        const criteria = this.normalizeCriteria(this.criteria)
        const response = await this.$store.dispatch('registration/filterParticipants', {
          event_uuid: this.eventUuid,
          criteria
        })
        const records = response?.data?.records || []
        this.previewTotal = response?.data?.total || 0
        this.printQueue = records.map((record: any) => ({
          participant_uuid: record.uuid,
          first_name: record.first_name,
          last_name: record.last_name,
          company: record.company,
          status: record.status,
          registration_type: record.registration_type
        }))
        this.resetPrintProgress()
        this.hasPreviewed = true
        this.previewStale = false
      } catch (error: any) {
        if (error?.status === 422) {
          this.previewError = error?.data?.error_message || 'Invalid criteria.'
        } else {
          Container.get(Notification).error('Error loading participant count.')
        }
      } finally {
        this.previewLoading = false
      }
    },

    /**
     * Create a point-in-time search snapshot for the current criteria and open the
     * attendees list for it in a new tab. Created fresh on every click so the snapshot
     * reflects what is currently on screen.
     */
    async openPreviewList () {
      try {
        const criteria = this.normalizeCriteria(this.criteria)
        const response = await this.$store.dispatch('registration/doSearch', {
          event_uuid: this.eventUuid,
          data: criteria
        })
        const uuid = response?.data?.uuid
        if (!uuid) return
        const route = this.$router.resolve({
          name: 'level-two.event.attendees.participant-search',
          params: { event_uuid: this.eventUuid as string, participant_search_uuid: uuid }
        })
        window.open(route.href, '_blank')
      } catch (error) {
        Container.get(Notification).error('Error creating preview list.')
      }
    }
  }
})
</script>

<template>
  <div>
    <component :is="providerComponent" ref="provider" />
    <template v-if="badgesEnabledAndAllowed">
      <slot :attrs="{ disabled: disabled || initializing || printLoopActive }" :on="{ click: () => openDialog() }">
        <v-btn class="priority-default" large :disabled="disabled || initializing || printLoopActive" @click="openDialog()">
          <v-icon>mdi-printer-outline</v-icon>
          Print Badges
        </v-btn>
      </slot>
    </template>
    <notes-warning-modal :visible="notesWarningShow" :notes="notesWarningContent" @confirm="resolveNotesWarning(true)" @cancel="resolveNotesWarning(false)" />
    <gtr-modal title="Print Badges" :value="showModal" :width="900" :closable="!printing" @close="closeModal">
      <div v-if="initializing" class="result-banner result-progress">
        <v-progress-circular indeterminate size="20" width="2" color="var(--teal-200)" />
        <span>Preparing the print service...</span>
      </div>
      <template v-else-if="printServiceEnabled">
        <v-tabs v-model="activeTab">
          <v-tab>Single Attendee</v-tab>
          <v-tab>Print Multiple</v-tab>
        </v-tabs>
        <v-tabs-items v-model="activeTab" class="mt-5">
          <!-- Single Attendee -->
          <v-tab-item>
            <div v-if="printing" class="result-banner result-progress">
              <v-progress-circular indeterminate size="20" width="2" color="var(--teal-200)" />
              <span>Printing in progress. Please keep this window open.</span>
            </div>
            <div v-else-if="printResult" :class="['result-banner', printResult.success ? 'result-success' : 'result-warning']">
              <v-icon :color="printResult.success ? 'var(--a2z-success)' : 'var(--a2z-accent)'">
                {{ printResult.success ? 'mdi-check-circle' : 'mdi-alert-circle' }}
              </v-icon>
              <span v-if="printResult.success">Badge printed successfully</span>
              <span v-else>Print error: {{ printResult.message }}</span>
            </div>

            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="searchTerm"
                  @input="onSearchInput"
                  label="Search by name or email"
                  prepend-inner-icon="mdi-magnify"
                  clearable
                  hide-details
                  outlined
                  dense
                  :disabled="printing"
                  class="mt-1 mb-2"
                />

                <div v-if="searching" class="search-status">
                  <v-progress-circular indeterminate size="20" width="2" color="var(--a2z-primary)" />
                  <span class="ml-2">Searching&hellip;</span>
                </div>

                <div v-else-if="tooManyResults" class="search-status">
                  Too many results &mdash; please refine your search.
                </div>

                <gtr-empty-state
                  v-else-if="hasSearched && searchResults.length === 0"
                  message="No participants found."
                  icon="empty-chairs.svg"
                />

                <div v-else-if="searchResults.length" class="search-results my-5">
                  <div
                    v-for="item in searchResults"
                    :key="item.id"
                    class="search-result-row"
                    :class="{ selected: isSelected(item) }"
                    @click="selectParticipant(item)"
                  >
                    <div class="result-main">
                      <div class="result-name">{{ item.full_name }}</div>
                      <div class="result-details">{{ item.email }}<span v-if="item.company"> &middot; {{ item.company }}</span><span v-if="item.registration_type"> &middot; {{ item.registration_type }}</span></div>
                    </div>
                    <div class="result-eligibility" :class="isEligible(item) ? 'eligible' : 'ineligible'">
                      <div>{{ isEligible(item) ? 'Eligible' : 'Ineligible' }}</div>
                      <div v-if="!isEligible(item)">
                        <span v-if="item.status !== 'Complete'">{{ item.status }}</span>
                        <span v-if="item.payment_status !== 'Paid'">{{ item.payment_status }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <v-card elevation="0" color="var(--a2z-primary-bg)" class="my-5">
                  <v-container>
                    <v-row align="center">
                      <v-col cols="4">
                        Printer:
                      </v-col>
                      <v-col>
                        <printer-select v-model="selectedPrinter" :items="availablePrinters" :disabled="printing" />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card>
              </v-col>

              <v-col cols="12" md="6">
                <div v-if="selectedParticipant" class="badge-preview-section">
                  <div class="preview-label">Badge Preview &mdash; {{ selectedParticipant.full_name }}</div>
                  <gtr-badge-preview
                    v-if="loadingPreview || badgePreviewSvg"
                    :badge="badgePreviewSvg || ''"
                    :loading="loadingPreview"
                    :include-frame="true"
                  />
                  <div v-else class="preview-unavailable">
                    <v-icon small class="mr-1">mdi-image-off-outline</v-icon>
                    No badge available for this participant.
                  </div>
                </div>
              </v-col>
            </v-row>

            <v-row align="center">
              <v-spacer />
              <v-col v-if="printResult" cols="auto">
                <v-btn class="priority-focus" @click="showModal = false">Done</v-btn>
              </v-col>
              <v-col v-else cols="auto">
                <v-btn class="priority-default mr-2" large @click="cancel">Cancel</v-btn>
                <v-btn class="priority-focus" large :loading="printing || notesCheckInFlight" :disabled="!selectedParticipant" @click="printBadge">
                  <v-icon>mdi-printer-outline</v-icon>
                  Print Badge
                </v-btn>
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- Print Multiple -->
          <v-tab-item>
            <gtr-show-if
              id="bulk-print-show-if"
              :model-override="criteria"
              @show-if-change="Object.assign(criteria, $event)"
              :hide-delete-entire-show-if-button="true"
              :readonly="printed > 0"
            />

            <div class="preview-count-row">
              <v-btn class="priority-default" :loading="previewLoading" :disabled="printed > 0" @click="previewCount">Preview Count</v-btn>
              <span v-if="previewError" class="preview-error">{{ previewError }}</span>
            </div>

            <template v-if="hasPreviewed">
              <v-row v-show="(printing || pausePrinting) && canDrillDown">
                <v-col cols="12" class="text-h6">
                  Now Printing: {{ currentParticipantName }}
                </v-col>
              </v-row>

              <div v-if="printLoopActive" class="result-banner result-progress">
                <v-progress-circular indeterminate size="20" width="2" color="var(--teal-200)" />
                <span>{{ printing ? 'Printing in progress' : 'Pausing after the current badge' }}<template v-if="currentParticipantName">: {{ currentParticipantName }}</template>. Please keep this window open.</span>
              </div>
              <div v-if="haltMessage && !printing" class="result-banner result-warning">
                <v-icon color="var(--a2z-accent)">mdi-alert-circle</v-icon>
                <span>{{ haltMessage }} {{ printed }} badge{{ printed === 1 ? '' : 's' }} printed.</span>
              </div>

              <v-row justify="center">
                <v-col cols="6">
                  <v-card
                    elevation="0"
                    class="stat-card"
                    :class="{ dimmed: previewStale, clickable: canDrillDown && queuedDrillItems.length > 0 }"
                    @click="canDrillDown && queuedDrillItems.length > 0 && toggleDrill('primary')"
                  >
                    <v-card-text>
                      <h6 class="text-uppercase">Queued to Print</h6>
                      <span class="text-incomplete">{{ queuedToPrintCount }}</span>
                      <div v-if="canDrillDown" class="stat-hint">
                        <template v-if="queuedDrillItems.length > 0">
                          <v-icon x-small>mdi-information-outline</v-icon> Click to see list
                        </template>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="6">
                  <v-card
                    elevation="0"
                    class="stat-card"
                    :class="{ clickable: canDrillDown && printedItems.length > 0 }"
                    @click="canDrillDown && printedItems.length > 0 && toggleDrill('secondary')"
                  >
                    <v-card-text>
                      <h6 class="text-uppercase">Already Printed</h6>
                      <span class="text-complete">{{ printed }}</span>
                      <div v-if="canDrillDown" class="stat-hint">
                        <template v-if="printedItems.length > 0">
                          <v-icon x-small>mdi-information-outline</v-icon> Click to see list
                        </template>
                        <template v-else>None yet</template>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <v-expand-transition>
                <div v-if="activeMultiDrillItems" class="drill-panel">
                  <div class="drill-panel-head">
                    <span>{{ expandedDrill === 'primary' ? 'Queued to Print' : 'Already Printed' }}</span>
                    <v-btn text x-small @click="expandedDrill = null">Collapse</v-btn>
                  </div>
                  <div class="drill-list">
                    <div v-for="item in activeMultiDrillItems" :key="item.participant_uuid" class="drill-item">
                      <v-avatar size="32" color="#C4C4C4" class="mr-2">
                        <span class="white--text">{{ getInitials(item) }}</span>
                      </v-avatar>
                      <span class="drill-name">{{ getParticipantName(item) }}</span>
                      <span v-if="item.registration_type" class="drill-type">{{ item.registration_type }}</span>
                    </div>
                  </div>
                </div>
              </v-expand-transition>

              <div class="criteria-summary">
                <span>{{ criteriaSummary }}</span>
                <a href="#" @click.prevent="openPreviewList">Preview list</a>
              </div>

              <v-card elevation="0" color="var(--a2z-primary-bg)" class="my-5" style="border-radius: 8px;">
                <v-container>
                  <v-row align="center">
                    <v-col cols="3">
                      Printer:
                    </v-col>
                    <v-col>
                      <printer-select v-model="selectedPrinter" :items="availablePrinters" :disabled="printing" />
                    </v-col>
                  </v-row>
                  <print-order-control
                    :sort-fields.sync="sortFields"
                    :sort-desc.sync="sortDesc"
                    :fields="sortableFields"
                    :disabled="printing || pausePrinting"
                  />
                </v-container>
              </v-card>

              <v-row align="center">
                <v-spacer />
                <v-col v-if="jobDone" cols="auto">
                  <v-btn class="priority-focus" @click="showModal = false">Done</v-btn>
                </v-col>
                <v-col v-else-if="printing" cols="auto">
                  <v-btn class="priority-danger" @click="pause">Pause Printing</v-btn>
                </v-col>
                <v-col v-else cols="auto">
                  <v-btn v-show="!pausePrinting" class="priority-default transparent-btn" @click="cancel">Cancel</v-btn>
                  <v-btn v-show="!pausePrinting" class="priority-focus" :disabled="!canStartBulkPrint" @click="printAll">
                    Print {{ previewTotal }} Badge{{ previewTotal === 1 ? '' : 's' }}
                  </v-btn>
                  <v-btn v-show="pausePrinting" class="priority-default mr-3" :disabled="printLoopActive" @click="printAll">
                    Print All {{ itemsToPrint.length }}
                  </v-btn>
                  <v-btn v-show="pausePrinting" class="priority-focus" :disabled="printLoopActive" @click="print">Resume Printing</v-btn>
                </v-col>
              </v-row>
            </template>
          </v-tab-item>
        </v-tabs-items>
      </template>
      <template v-else-if="!useMps">
        <div class="d-flex align-center">
          <div class="mr-3">
            <v-icon color="var(--a2z-danger)">mdi-printer-off</v-icon>
          </div>
          <div>
            The ScriptX service is not running or is not installed.<br />Please either start the service or <a href="https://support.meadroid.com/Downloads/ScriptXServices/Download" target="_blank" @click="showModal = false">download</a> and install it, and then try again.<br />Alternatively, switch to using the Momentive Print Service.
          </div>
        </div>
      </template>
      <template v-else>
        <div class="d-flex align-center">
          <div class="mr-3">
            <v-icon color="var(--a2z-danger)">mdi-printer-off</v-icon>
          </div>
          <div>
            The Momentive Print Service is not running or is not installed. Please contact support for assistance or switch to using ScriptX.
          </div>
        </div>
      </template>
    </gtr-modal>
  </div>
</template>

<style scoped>
.priority-default.transparent-btn {
  background: transparent !important;
  border: 1px solid #00000033;
  border-radius: 8px;
  margin-right: 8px;
  color: black !important;
}

.result-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-weight: 500;
}

.result-success {
  background-color: rgba(40, 167, 69, 0.1);
  color: var(--a2z-success);
}

.result-warning {
  background-color: rgba(242, 101, 34, 0.1);
  color: var(--a2z-accent);
}

.result-progress {
  background-color: var(--a2z-primary-bg);
}

.search-status {
  display: flex;
  align-items: center;
  padding: 12px 4px;
  opacity: 0.7;
}

.search-results {
  max-height: 210px;
  overflow-y: auto;
  border: 1px solid #00000033;
  border-radius: 8px;
}

.search-result-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid #00000014;
  cursor: pointer;
}

.search-result-row:last-child {
  border-bottom: none;
}

.search-result-row:hover {
  background-color: #00000008;
}

.search-result-row.selected {
  background-color: var(--a2z-primary-bg);
  border-left: 3px solid var(--a2z-primary);
}

.result-main {
  min-width: 0;
  flex: 1;
}

.result-name {
  font-weight: 700;
}

.result-details {
  opacity: 0.7;
  font-size: .875em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-eligibility {
  flex-shrink: 0;
  margin-left: 12px;
  font-size: .8em;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 12px;
  text-align: center;
}

.result-eligibility.eligible {
  color: var(--a2z-success);
  background-color: rgba(40, 167, 69, 0.1);
}

.result-eligibility.ineligible {
  color: var(--a2z-accent);
  background-color: rgba(242, 101, 34, 0.1);
}

.result-eligibility:first-child {
  font-weight: bold;
}

.badge-preview-section {
  margin: 16px 0;
}

.preview-label {
  font-size: 0.8em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.6;
  margin-bottom: 8px;
}

.preview-unavailable {
  display: flex;
  align-items: center;
  padding: 24px;
  background-color: var(--a2z-primary-bg);
  border-radius: 8px;
  font-size: 0.9em;
  opacity: 0.7;
}

.preview-count-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 16px 0;
}

.preview-error {
  color: var(--a2z-danger);
  font-size: 0.9em;
}

.criteria-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 4px;
  font-size: 0.9em;
  opacity: 0.85;
}

h6 {
  opacity: 0.6;
}

.stat-card {
  border: 1px solid #00000033;
  border-radius: 8px;
}

.stat-card h6 {
  font-size: .95em;
  font-weight: 400;
  letter-spacing: 1.2px;
  margin-bottom: 8px;
}

.stat-card span {
  font-size: 1.5em;
}

.stat-card.dimmed {
  opacity: 0.5;
}

.stat-card.clickable {
  cursor: pointer;
}

.stat-card.clickable:hover {
  border-color: #00000066;
}

.stat-hint {
  font-size: .75em;
  opacity: 0.6;
  margin-top: 4px;
}

.drill-panel {
  border: 1px solid #00000033;
  border-radius: 8px;
  margin-top: 12px;
}

.drill-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  font-weight: 500;
  border-bottom: 1px solid #00000033;
}

.drill-list {
  max-height: 240px;
  overflow-y: auto;
}

.drill-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #00000014;
}

.drill-item:last-child {
  border-bottom: none;
}

.drill-name {
  flex: 1;
}

.drill-type {
  opacity: 0.6;
  font-size: .9em;
}
</style>
